import React from "react";
import { ModalProps } from "react-bootstrap";
import { X } from "react-bootstrap-icons";
import { useFormik } from "formik";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import { BaseButton, Error, IMemberPlan, Switch, TextInput, selectTheme } from "../../../libs";
import { CouponFormValues } from "../types";
import * as Styled from "./CouponModal.styled";

interface Props extends ModalProps {
  plans: IMemberPlan[];
  onSave: (values: Record<string, unknown>) => Promise<void>;
}

export const CouponModal: React.FC<Props> = ({ plans, onSave, ...props }) => {
  const formik = useFormik<CouponFormValues>({
    initialValues: {
      name: "",
      code: "",
      type: "PERCENT",
      percentOff: "",
      amountOff: "",
      duration: "ONCE",
      durationInMonths: "",
      appliesToAllPlans: true,
      eligiblePlanIds: [],
      startAt: null,
      endAt: null,
      maxRedemptions: "",
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.name.trim()) errors.name = "* Coupon name is required";
      if (!values.code.trim()) errors.code = "* Coupon code is required";
      if (
        values.type === "PERCENT" &&
        (!(Number(values.percentOff) > 0) || Number(values.percentOff) >= 100)
      )
        errors.percentOff = "* Enter a percentage from 1 to 99";
      if (values.type === "FIXED" && !(Number(values.amountOff) > 0))
        errors.amountOff = "* Enter an amount greater than zero";
      if (values.duration === "REPEATING" && !(Number(values.durationInMonths) > 0))
        errors.durationInMonths = "* Enter the number of months";
      if (!values.appliesToAllPlans && values.eligiblePlanIds.length === 0)
        errors.eligiblePlanIds = "* Select at least one plan";
      if (values.startAt && values.endAt && values.endAt <= values.startAt)
        errors.endAt = "* End date must be after the start date";
      if (
        values.maxRedemptions &&
        (!Number.isInteger(Number(values.maxRedemptions)) || !(Number(values.maxRedemptions) > 0))
      )
        errors.maxRedemptions = "* Maximum redemptions must be a positive whole number";
      return errors;
    },
    onSubmit: async (values, helpers) => {
      try {
        await onSave({
          ...values,
          code: values.code.trim().toUpperCase(),
          percentOff: values.type === "PERCENT" ? Number(values.percentOff) : null,
          amountOff: values.type === "FIXED" ? Number(values.amountOff) : null,
          durationInMonths:
            values.duration === "REPEATING" ? Number(values.durationInMonths) : null,
          maxRedemptions: values.maxRedemptions ? Number(values.maxRedemptions) : null,
          startAt: values.startAt?.toISOString() || null,
          endAt: values.endAt?.toISOString() || null,
        });
        helpers.resetForm();
        props.onHide?.();
      } catch (error) {
        console.error("Error creating coupon:", error);
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });
  const typeOptions = [
    { value: "PERCENT", label: "Percentage discount" },
    { value: "FIXED", label: "Fixed amount (USD)" },
  ];
  const durationOptions = [
    { value: "ONCE", label: "Once" },
    { value: "FOREVER", label: "Forever" },
    { value: "REPEATING", label: "Multiple months" },
  ];
  const planOptions = plans.map((plan) => ({ value: plan.id, label: plan.name }));

  return (
    <Styled.CouponModal {...props} centered keyboard={false}>
      <Styled.ModalWrapper>
        <Styled.Header>
          <Styled.ModalTitle>Create Coupon Code</Styled.ModalTitle>
          <Styled.CloseButton
            onClick={() => {
              formik.resetForm();
              props.onHide?.();
            }}
            aria-label="Close"
          >
            <X size={22} />
          </Styled.CloseButton>
        </Styled.Header>
        <form onSubmit={formik.handleSubmit}>
          <Styled.FormGrid>
            <Styled.Field>
              {formik.touched.name && formik.errors.name && <Error message={formik.errors.name} />}
              <TextInput
                fieldName="name"
                fieldLabel="Coupon Name *"
                placeholder="Summer promotion"
                fieldValue={formik.values.name}
                onChangeHandler={formik.handleChange}
                hasError={!!(formik.touched.name && formik.errors.name)}
              />
            </Styled.Field>
            <Styled.Field>
              {formik.touched.code && formik.errors.code && <Error message={formik.errors.code} />}
              <TextInput
                fieldName="code"
                fieldLabel="Customer-facing Code *"
                placeholder="SUMMER30"
                fieldValue={formik.values.code}
                onChangeHandler={(event) =>
                  formik.setFieldValue(
                    "code",
                    (event.target as HTMLInputElement).value
                      .toUpperCase()
                      .replace(/[^A-Z0-9_-]/g, "")
                  )
                }
                hasError={!!(formik.touched.code && formik.errors.code)}
              />
            </Styled.Field>
            <Styled.Field>
              <Styled.FieldLabel>Discount Type</Styled.FieldLabel>
              <Select
                styles={selectTheme.singleSelect()}
                options={typeOptions}
                value={typeOptions.find((option) => option.value === formik.values.type)}
                onChange={(option) => formik.setFieldValue("type", option?.value)}
              />
            </Styled.Field>
            <Styled.Field>
              {formik.values.type === "PERCENT" ? (
                <>
                  {formik.errors.percentOff && <Error message={formik.errors.percentOff} />}
                  <TextInput
                    fieldName="percentOff"
                    fieldLabel="Percentage Off *"
                    fieldType="number"
                    placeholder="30"
                    fieldValue={formik.values.percentOff}
                    onChangeHandler={formik.handleChange}
                    hasError={!!formik.errors.percentOff}
                  />
                </>
              ) : (
                <>
                  {formik.errors.amountOff && <Error message={formik.errors.amountOff} />}
                  <TextInput
                    fieldName="amountOff"
                    fieldLabel="Amount Off (USD) *"
                    fieldType="number"
                    placeholder="10.00"
                    fieldValue={formik.values.amountOff}
                    onChangeHandler={formik.handleChange}
                    hasError={!!formik.errors.amountOff}
                  />
                </>
              )}
            </Styled.Field>
            <Styled.Field>
              <Styled.FieldLabel>Discount Duration</Styled.FieldLabel>
              <Select
                styles={selectTheme.singleSelect()}
                options={durationOptions}
                value={durationOptions.find((option) => option.value === formik.values.duration)}
                onChange={(option) => formik.setFieldValue("duration", option?.value)}
              />
            </Styled.Field>
            <Styled.Field>
              {formik.values.duration === "REPEATING" ? (
                <>
                  {formik.errors.durationInMonths && (
                    <Error message={formik.errors.durationInMonths} />
                  )}
                  <TextInput
                    fieldName="durationInMonths"
                    fieldLabel="Number of Months *"
                    fieldType="number"
                    placeholder="3"
                    fieldValue={formik.values.durationInMonths}
                    onChangeHandler={formik.handleChange}
                    hasError={!!formik.errors.durationInMonths}
                  />
                </>
              ) : (
                <>
                  <Styled.FieldLabel>Stripe behavior</Styled.FieldLabel>
                  <Styled.HelpText>
                    {formik.values.duration === "ONCE"
                      ? "Applied to the first eligible payment."
                      : "Applied to every eligible recurring payment."}
                  </Styled.HelpText>
                </>
              )}
            </Styled.Field>
            <Styled.Field>
              <Styled.FieldLabel>Start Date and Time</Styled.FieldLabel>
              <Styled.DatePickerWrapper>
                <Styled.CustomDatePicker
                  selected={formik.values.startAt}
                  onChange={(date: Date | null) => formik.setFieldValue("startAt", date)}
                  showTimeSelect
                  dateFormat="Pp"
                  placeholderText="Start immediately"
                  isClearable
                />
              </Styled.DatePickerWrapper>
            </Styled.Field>
            <Styled.Field>
              {formik.errors.endAt && <Error message={formik.errors.endAt} />}
              <Styled.FieldLabel>End Date and Time</Styled.FieldLabel>
              <Styled.DatePickerWrapper>
                <Styled.CustomDatePicker
                  selected={formik.values.endAt}
                  onChange={(date: Date | null) => formik.setFieldValue("endAt", date)}
                  minDate={formik.values.startAt || new Date()}
                  showTimeSelect
                  dateFormat="Pp"
                  placeholderText="No end date"
                  isClearable
                />
              </Styled.DatePickerWrapper>
            </Styled.Field>
            <Styled.Field>
              {formik.errors.maxRedemptions && <Error message={formik.errors.maxRedemptions} />}
              <TextInput
                fieldName="maxRedemptions"
                fieldLabel="Maximum Redemptions"
                fieldType="number"
                placeholder="Unlimited"
                fieldValue={formik.values.maxRedemptions}
                onChangeHandler={formik.handleChange}
                hasError={!!formik.errors.maxRedemptions}
              />
            </Styled.Field>
            <Styled.Field>
              <Styled.FieldLabel>Plan Eligibility</Styled.FieldLabel>
              <Styled.SwitchRow>
                <Styled.SwitchLabel>Apply to All Plans</Styled.SwitchLabel>
                <Switch
                  checked={formik.values.appliesToAllPlans}
                  onChange={(event) =>
                    formik.setFieldValue("appliesToAllPlans", event.target.checked)
                  }
                />
              </Styled.SwitchRow>
            </Styled.Field>
            {!formik.values.appliesToAllPlans && (
              <Styled.FullWidth>
                {formik.errors.eligiblePlanIds && (
                  <Error message={String(formik.errors.eligiblePlanIds)} />
                )}
                <Styled.FieldLabel>Eligible Plans</Styled.FieldLabel>
                <Select
                  isMulti
                  styles={selectTheme.multiSelect(!!formik.errors.eligiblePlanIds)}
                  options={planOptions}
                  value={planOptions.filter((option) =>
                    formik.values.eligiblePlanIds.includes(option.value)
                  )}
                  onChange={(options) =>
                    formik.setFieldValue(
                      "eligiblePlanIds",
                      options.map((option) => option.value)
                    )
                  }
                  placeholder="Select membership plans"
                />
              </Styled.FullWidth>
            )}
          </Styled.FormGrid>
          <Styled.Footer>
            <Styled.ButtonSlot>
              <BaseButton
                variant="secondary"
                onClick={() => {
                  formik.resetForm();
                  props.onHide?.();
                }}
              >
                Cancel
              </BaseButton>
            </Styled.ButtonSlot>
            <Styled.ButtonSlot>
              <BaseButton type="submit" disabled={formik.isSubmitting}>
                {formik.isSubmitting ? "Creating..." : "Create Coupon"}
              </BaseButton>
            </Styled.ButtonSlot>
          </Styled.Footer>
        </form>
      </Styled.ModalWrapper>
    </Styled.CouponModal>
  );
};
